import { supabase } from '@/lib/supabaseClient';

export interface Tenant {
  tenant_id: string;
  company_name: string;
  contact_name: string;
  contact_phone?: string | null;
  contact_email: string;
  email_config?: any;
  created_at: string;
  updated_at: string;
}

export interface CreateTenantData {
  company_name: string;
  contact_name: string;
  contact_email: string;
  contact_phone?: string;
}

export interface CreateTenantResponse {
  success: boolean;
  tenant?: Tenant;
  error?: string;
  message?: string;
}

/**
 * Creates a new tenant with default initialization
 * @param data Tenant creation data
 * @returns Response with tenant details or error
 */
export async function createTenant(
  data: CreateTenantData
): Promise<CreateTenantResponse> {
  try {
    // Validate input
    if (!data.company_name || data.company_name.trim() === '') {
      return {
        success: false,
        error: 'Company name is required',
      };
    }

    if (!data.contact_name || data.contact_name.trim() === '') {
      return {
        success: false,
        error: 'Contact name is required',
      };
    }

    if (!data.contact_email || data.contact_email.trim() === '') {
      return {
        success: false,
        error: 'Contact email is required',
      };
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(data.contact_email)) {
      return {
        success: false,
        error: 'Invalid email format',
      };
    }

    // Call database function to create tenant
    const { data: result, error } = await supabase.rpc('create_tenant', {
      p_company_name: data.company_name,
      p_contact_name: data.contact_name,
      p_contact_email: data.contact_email,
      p_contact_phone: data.contact_phone || null,
    });

    if (error) {
      console.error('[tenantService] Error creating tenant:', error);
      return {
        success: false,
        error: error.message,
      };
    }

    // Check if result indicates success
    if (result && result.success) {
      return {
        success: true,
        tenant: result.tenant,
        message: result.message || 'Tenant created successfully',
      };
    } else {
      return {
        success: false,
        error: result?.error || 'Failed to create tenant',
        message: result?.message,
      };
    }
  } catch (error: any) {
    console.error('[tenantService] Exception creating tenant:', error);
    return {
      success: false,
      error: error.message || 'An unexpected error occurred',
    };
  }
}

/**
 * Lists all tenants (superadmin only)
 * @returns Array of tenants
 */
export async function listTenants(): Promise<Tenant[]> {
  try {
    const { data, error } = await supabase
      .from('tenants')
      .select('*')
      .order('company_name');

    if (error) {
      console.error('[tenantService] Error listing tenants:', error);
      throw error;
    }

    return data || [];
  } catch (error) {
    console.error('[tenantService] Exception listing tenants:', error);
    throw error;
  }
}

/**
 * Gets details for a specific tenant
 * @param tenantId The tenant UUID
 * @returns Tenant details or null if not found
 */
export async function getTenantDetails(
  tenantId: string
): Promise<Tenant | null> {
  try {
    const { data, error } = await supabase
      .from('tenants')
      .select('*')
      .eq('tenant_id', tenantId)
      .single();

    if (error) {
      if (error.code === 'PGRST116') {
        // Not found
        return null;
      }
      console.error('[tenantService] Error getting tenant details:', error);
      throw error;
    }

    return data;
  } catch (error) {
    console.error('[tenantService] Exception getting tenant details:', error);
    throw error;
  }
}

/**
 * Updates tenant information
 * @param tenantId The tenant UUID
 * @param updates Partial tenant data to update
 * @returns True if update was successful
 */
export async function updateTenant(
  tenantId: string,
  updates: Partial<Omit<Tenant, 'tenant_id' | 'created_at' | 'updated_at'>>
): Promise<boolean> {
  try {
    const { error } = await supabase
      .from('tenants')
      .update({
        ...updates,
        updated_at: new Date().toISOString(),
      })
      .eq('tenant_id', tenantId);

    if (error) {
      console.error('[tenantService] Error updating tenant:', error);
      throw error;
    }

    return true;
  } catch (error) {
    console.error('[tenantService] Exception updating tenant:', error);
    throw error;
  }
}

export interface ArchiveTenantResponse {
  success: boolean;
  tenant_id?: string;
  company_name?: string;
  archived_at?: string;
  archived_by?: string;
  reason?: string;
  preserved_data?: {
    projects: number;
    pipelines: number;
    users_affected: number;
  };
  error?: string;
  message?: string;
}

export interface UnarchiveTenantResponse {
  success: boolean;
  tenant_id?: string;
  company_name?: string;
  restored_at?: string;
  users_reactivated?: number;
  error?: string;
  message?: string;
}

export interface ArchivedTenant {
  tenant_id: string;
  company_name: string;
  contact_email: string;
  archived_at: string;
  archived_by_email: string | null;
  project_count: number;
  user_count: number;
}

export interface HardDeleteTenantResponse {
  success: boolean;
  tenant_id?: string;
  company_name?: string;
  deleted?: {
    projects: number;
    pipelines: number;
    stages: number;
    checklist_templates: number;
    notification_templates: number;
    teams: number;
    user_roles: number;
  };
  error?: string;
  message?: string;
}

/**
 * Archives a tenant (soft delete) - removes access but preserves all data
 * This will:
 * - Mark tenant as archived
 * - Deactivate all user role assignments (removes access)
 * - Clear active superadmin sessions
 * - Preserve ALL data (projects, pipelines, stages, etc.)
 *
 * Can be restored later using unarchiveTenant()
 *
 * @param tenantId The tenant UUID
 * @param reason Optional reason for archiving
 * @returns Response with archive details or error
 */
export async function archiveTenant(
  tenantId: string,
  reason?: string
): Promise<ArchiveTenantResponse> {
  try {
    const { data: result, error } = await supabase.rpc('archive_tenant', {
      p_tenant_id: tenantId,
      p_reason: reason || null,
    });

    if (error) {
      console.error('[tenantService] Error archiving tenant:', error);
      return {
        success: false,
        error: error.message,
      };
    }

    if (result && result.success) {
      return {
        success: true,
        tenant_id: result.tenant_id,
        company_name: result.company_name,
        archived_at: result.archived_at,
        archived_by: result.archived_by,
        reason: result.reason,
        preserved_data: result.preserved_data,
        message: result.message || 'Tenant archived successfully',
      };
    } else {
      return {
        success: false,
        error: result?.error || 'Failed to archive tenant',
        message: result?.message,
      };
    }
  } catch (error: any) {
    console.error('[tenantService] Exception archiving tenant:', error);
    return {
      success: false,
      error: error.message || 'An unexpected error occurred',
    };
  }
}

/**
 * Restores an archived tenant (unarchive)
 * This will:
 * - Remove archived flag
 * - Reactivate all user role assignments
 * - Restore full access to the tenant
 *
 * @param tenantId The tenant UUID
 * @returns Response with restore details or error
 */
export async function unarchiveTenant(
  tenantId: string
): Promise<UnarchiveTenantResponse> {
  try {
    const { data: result, error } = await supabase.rpc('unarchive_tenant', {
      p_tenant_id: tenantId,
    });

    if (error) {
      console.error('[tenantService] Error unarchiving tenant:', error);
      return {
        success: false,
        error: error.message,
      };
    }

    if (result && result.success) {
      return {
        success: true,
        tenant_id: result.tenant_id,
        company_name: result.company_name,
        restored_at: result.restored_at,
        users_reactivated: result.users_reactivated,
        message: result.message || 'Tenant restored successfully',
      };
    } else {
      return {
        success: false,
        error: result?.error || 'Failed to unarchive tenant',
        message: result?.message,
      };
    }
  } catch (error: any) {
    console.error('[tenantService] Exception unarchiving tenant:', error);
    return {
      success: false,
      error: error.message || 'An unexpected error occurred',
    };
  }
}

/**
 * Lists all archived tenants with metadata
 * @returns Array of archived tenants
 */
export async function listArchivedTenants(): Promise<ArchivedTenant[]> {
  try {
    const { data, error } = await supabase.rpc('list_archived_tenants');

    if (error) {
      console.error('[tenantService] Error listing archived tenants:', error);
      throw error;
    }

    return data || [];
  } catch (error) {
    console.error('[tenantService] Exception listing archived tenants:', error);
    throw error;
  }
}

/**
 * PERMANENTLY deletes a tenant and ALL associated data (use with EXTREME caution)
 * This will permanently delete:
 * - All projects
 * - All pipelines and stages
 * - All checklist templates
 * - All notification templates
 * - All teams
 * - All user role assignments
 * - Theme settings
 * - The tenant record itself
 *
 * ⚠️ WARNING: This CANNOT be undone. Consider using archiveTenant() instead.
 *
 * @param tenantId The tenant UUID
 * @returns Response with deletion details or error
 */
export async function hardDeleteTenant(
  tenantId: string
): Promise<HardDeleteTenantResponse> {
  try {
    // Call database function to permanently delete tenant and all associated data
    const { data: result, error } = await supabase.rpc('hard_delete_tenant', {
      p_tenant_id: tenantId,
    });

    if (error) {
      console.error('[tenantService] Error hard deleting tenant:', error);
      return {
        success: false,
        error: error.message,
      };
    }

    // Check if result indicates success
    if (result && result.success) {
      return {
        success: true,
        tenant_id: result.tenant_id,
        company_name: result.company_name,
        deleted: result.deleted,
        message: result.message || 'Tenant permanently deleted',
      };
    } else {
      return {
        success: false,
        error: result?.error || 'Failed to delete tenant',
        message: result?.message,
      };
    }
  } catch (error: any) {
    console.error('[tenantService] Exception hard deleting tenant:', error);
    return {
      success: false,
      error: error.message || 'An unexpected error occurred',
    };
  }
}

/**
 * Checks if a company name is already taken
 * @param companyName The company name to check
 * @returns True if the name is available, false if taken
 */
export async function isCompanyNameAvailable(
  companyName: string
): Promise<boolean> {
  try {
    const { data, error } = await supabase
      .from('tenants')
      .select('tenant_id')
      .ilike('company_name', companyName)
      .limit(1);

    if (error) {
      console.error('[tenantService] Error checking company name:', error);
      throw error;
    }

    return !data || data.length === 0;
  } catch (error) {
    console.error('[tenantService] Exception checking company name:', error);
    throw error;
  }
}
